/**
 * Topbar search — one box for everything an admin looks up.
 *
 * Two kinds of result, in one list:
 *
 *   Pages    matched against the sidebar, instantly, with no request.
 *   Records  students, batches and trainers, fetched from the
 *            same endpoints their list pages use, debounced.
 *
 * It only ever searches what the signed-in account is allowed to open:
 * each source is gated on the same permission its menu entry is, so the
 * box never surfaces a record the user would be refused on click.
 *
 * Written from scratch on the shared tokens. No react-bootstrap, no
 * vendor classes, no !important.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CornerDownLeft,
  GraduationCap,
  LayoutGrid,
  Search,
  UserCheck,
  UserCog,
  X,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { MenuList } from './Menu';
import { getAllStudents } from '../../services/StudentFromBillingService';
import { getBatches } from '../../services/BatchService';
import { getTrainers } from '../../services/TrainerService';
import { getUsers } from '../../services/UserSerivce';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 350;
const PER_SOURCE = 5;

/* Flatten the sidebar once — the page results come from here. */
const PAGES = MenuList.flatMap((item) => {
  const own = item.to ? [{ title: item.title, to: item.to, key: item.eventKey, group: 'Dashboard' }] : [];
  const kids = (item.content || [])
    .filter((c) => c.to)
    .map((c) => ({ title: c.title, to: c.to, key: c.eventKey, group: item.title }));
  return [...own, ...kids];
});

/**
 * Each record source: the permission that gates it, the call, how to read
 * a row, and the list page a hit opens. Adding a source means adding one
 * entry, not editing four places.
 *
 * Courses are deliberately absent: the sidebar's "Course Modules" entry
 * points at `list-course`, which is not a registered route, so a course
 * hit would land on the 404 page. Add it back once that route exists.
 */
const SOURCES = [
  {
    id: 'students',
    label: 'Students',
    module: 'all-students',
    route: 'all-students',
    icon: <GraduationCap size={14} />,
    /* The trailing false turns OFF the receiptStatus=true this service
       otherwise bakes into the URL. Searching for someone should find
       them whether or not they have been billed; with it on, a student
       without a receipt returned nothing for their name or their email
       and the box looked broken. */
    fetch: (q) => getAllStudents(1, PER_SOURCE, q, 'all', '', '', false),
    rows: (res) => res?.data ?? [],
    map: (r) => ({ id: r.id, title: r.name, note: [r.email, r.phone].filter(Boolean).join(' · ') }),
  },
  {
    id: 'batches',
    label: 'Batches',
    module: 'batches',
    route: 'batches',
    icon: <LayoutGrid size={14} />,
    fetch: (q) => getBatches(PER_SOURCE, 1, q, 'name'),
    rows: (res) => res?.data ?? [],
    map: (r) => ({ id: r.id, title: r.name, note: [r.courseName, r.mode].filter(Boolean).join(' · ') }),
  },
  {
    id: 'trainers',
    label: 'Trainers',
    module: 'list-trainer',
    route: 'list-trainer',
    icon: <UserCheck size={14} />,
    fetch: (q) => getTrainers(PER_SOURCE, 1, q),
    rows: (res) => res?.data ?? [],
    /* trainerName / trainerEmail are the fields this collection actually
       stores -- `name` and `email` are undefined on a trainer record, so
       every hit rendered as a blank row with no title. The backend was
       matching the email all along; there was just nothing to show for
       it. Fallbacks kept because some endpoints do return the short
       names. */
    map: (r) => ({
      id: r.id,
      title: r.trainerName || r.name || 'Unnamed trainer',
      note: [r.trainerEmail || r.email, r.specialization].filter(Boolean).join(' · '),
    }),
  },
  {
    id: 'users',
    label: 'Users',
    route: 'setting/List-user',
    icon: <UserCog size={14} />,
    /* Added because the box searched students, batches and trainers but
       not users at all -- so a user's email matched nothing, no matter
       what was typed.

       `allow` instead of `module`: the user list is not registered as a
       permission the way all-students or list-trainer are, so the usual
       hasPermission() gate would be false for everyone and this source
       would never run. It is reached from the Settings menu in the
       header, which is gated on isAdmin -- so it is gated on isAdmin
       here too, and the two agree about who may see it. */
    allow: ({ isAdmin }) => isAdmin,
    fetch: (q) => getUsers(PER_SOURCE, 1, q),
    rows: (res) => res?.data ?? [],
    map: (r) => ({
      id: r.id,
      title: r.name || [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || 'Unnamed user',
      note: [r.email, r.mobile || r.phone].filter(Boolean).join(' · '),
    }),
  },
];

export default function TopbarSearch() {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  // Same derivation the header uses for its Settings menu, so the two
  // agree about who counts as an admin.
  const userData = user?.user || user;
  const isAdmin =
    Boolean(userData?.isSuperAdmin) ||
    String(userData?.profileIds || '').toLowerCase() === 'admin';

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  // Every fetch carries a sequence number so a slow early response cannot
  // land on top of the results for a longer query typed after it.
  const seq = useRef(0);

  const allowedSources = useMemo(
    () =>
      SOURCES.filter((s) =>
        // A source may bring its own gate when it has no permission key of
        // its own to be checked against -- see the users source.
        s.allow
          ? s.allow({ hasPermission, isAdmin })
          : hasPermission(s.module, 'view', 'lms') || hasPermission(s.module, 'list', 'lms'),
      ),
    [hasPermission, isAdmin]
  );

  const pages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < MIN_CHARS) return [];
    return PAGES
      .filter((p) => p.title.toLowerCase().includes(q))
      .filter((p) => hasPermission(p.key, 'view', 'lms') || hasPermission(p.key, 'list', 'lms'))
      .slice(0, 5);
  }, [query, hasPermission]);

  /* ── Fetch records, debounced ── */
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_CHARS || allowedSources.length === 0) {
      setRecords([]);
      setBusy(false);
      return undefined;
    }

    const mine = ++seq.current;
    setBusy(true);

    const timer = setTimeout(async () => {
      const settled = await Promise.allSettled(
        allowedSources.map(async (s) => ({ source: s, rows: s.rows(await s.fetch(q)) }))
      );
      if (mine !== seq.current) return; // a newer query is already in flight

      const groups = settled
        .filter((r) => r.status === 'fulfilled' && r.value.rows.length > 0)
        .map(({ value }) => ({
          source: value.source,
          items: value.rows.slice(0, PER_SOURCE).map(value.source.map),
        }));

      setRecords(groups);
      setBusy(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, allowedSources]);

  /* ── One flat list, so the arrow keys have something to walk ── */
  const flat = useMemo(() => {
    const rows = pages.map((p) => ({ kind: 'page', to: p.to, title: p.title, note: p.group }));
    records.forEach((g) => {
      g.items.forEach((item) => {
        rows.push({
          kind: 'record',
          to: g.source.route,
          title: item.title,
          note: item.note,
          badge: g.source.label,
        });
      });
    });
    return rows;
  }, [pages, records]);

  useEffect(() => { setCursor(0); }, [flat.length]);

  const go = useCallback((row) => {
    if (!row) return;
    setOpen(false);
    setQuery('');
    // The list pages read their own filters; this lands on the right page
    // and carries the term so the page can pick it up rather than
    // pretending to deep-link to a record that has no detail route.
    navigate(`/${row.to}${row.kind === 'record' ? `?search=${encodeURIComponent(row.title)}` : ''}`);
  }, [navigate]);

  /* ── Ctrl/Cmd-K focuses the box from anywhere ── */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── Click outside closes ── */
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % flat.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c - 1 + flat.length) % flat.length); }
    if (e.key === 'Enter') { e.preventDefault(); go(flat[cursor]); }
  };

  // Nothing to search means no box, rather than a box that finds nothing.
  if (allowedSources.length === 0 && PAGES.length === 0) return null;

  const short = query.trim().length > 0 && query.trim().length < MIN_CHARS;
  let index = -1;

  return (
    <div className="smt-search" ref={boxRef}>
      <div className={`smt-search-box${open ? ' on' : ''}`}>
        <Search size={16} />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Search students, batches, trainers, pages…"
          aria-label="Search"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button type="button" className="smt-search-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            aria-label="Clear search">
            <X size={14} />
          </button>
        ) : (
          <kbd className="smt-search-kbd">Ctrl K</kbd>
        )}
      </div>

      {open && query.trim().length > 0 && (
        <div className="smt-search-panel" role="listbox">
          {short ? (
            <p className="smt-search-hint">Keep typing — {MIN_CHARS} characters minimum.</p>
          ) : (
            <>
              {pages.length > 0 && (
                <>
                  <div className="smt-search-group">Pages</div>
                  {pages.map((p) => {
                    index += 1;
                    const i = index;
                    return (
                      <button key={`page-${p.to}-${p.title}`} type="button" role="option" aria-selected={i === cursor}
                        className={`smt-search-row${i === cursor ? ' on' : ''}`}
                        onMouseEnter={() => setCursor(i)} onClick={() => go(flat[i])}>
                        <span className="smt-search-icon"><LayoutGrid size={14} /></span>
                        <span className="smt-search-text">
                          <b>{p.title}</b>
                          <span>{p.group}</span>
                        </span>
                        <CornerDownLeft size={13} className="smt-search-enter" />
                      </button>
                    );
                  })}
                </>
              )}

              {records.map((g) => (
                <div key={g.source.id}>
                  <div className="smt-search-group">{g.source.label}</div>
                  {g.items.map((item) => {
                    index += 1;
                    const i = index;
                    return (
                      <button key={`${g.source.id}-${item.id}`} type="button" role="option" aria-selected={i === cursor}
                        className={`smt-search-row${i === cursor ? ' on' : ''}`}
                        onMouseEnter={() => setCursor(i)} onClick={() => go(flat[i])}>
                        <span className="smt-search-icon">{g.source.icon}</span>
                        <span className="smt-search-text">
                          <b>{item.title}</b>
                          {item.note && <span>{item.note}</span>}
                        </span>
                        <CornerDownLeft size={13} className="smt-search-enter" />
                      </button>
                    );
                  })}
                </div>
              ))}

              {busy && <p className="smt-search-hint">Searching…</p>}
              {!busy && flat.length === 0 && (
                <p className="smt-search-hint">Nothing matches “{query.trim()}”.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
